import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import { Modal, Input } from "antd";
import { MdOutlineSendToMobile } from "react-icons/md";
import {
  AiOutlineLoading3Quarters,
} from "react-icons/ai";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { config } from "../../config/config.jsx";
import CryptoJS from "crypto-js";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import LogoImg from "../../assets/images/Logo.png";
import IPEHistoryTable from "./History/IPEHistory.jsx";

function IPEClearance() {
  const navigate = useNavigate();

  /* ---------------------------- component state ---------------------------- */
  const [formData, setFormData] = useState({
    trackingId: "",
  });
  const [loading, setLoading] = useState(false);
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);
  const [amount, setAmount] = useState(1); // Default amount
  const [priceLoading, setPriceLoading] = useState(true);


  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  /* --------------------------------- render -------------------------------- */
  const showConfirmation = async () => {
    const result = await Swal.fire({
      title: "Confirm IPE Clearance",
      html: `
        <p class="mb-2 text-gray-500">Please confirm your details:</p>
        <p class="mb-2 text-gray-500">Tracking ID: ${formData.trackingId}</p>
        <p class="mb-2 text-gray-500">Amount: ₦${amount.toLocaleString()}</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, proceed",
      cancelButtonText: "Cancel",
    });

    return result.isConfirmed;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Show confirmation dialog
    const confirmed = await showConfirmation();
    if (!confirmed) return;

    // Get user ID from localStorage
    let userId = null;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userObj = decryptData(userStr);
        userId = userObj?._id || userObj?.id;
      }
    } catch {}

    setLoading(true);
    const payload = {
      trackingId: formData.trackingId,
      amount, // Use dynamic amount
      userId,
    };
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.ipeSubmit}`,
        payload,
        { withCredentials: true },
      );

      setVerificationResult(response.data?.data);
      setIsSuccessModalVisible(true);
      toast.success("IPE Clearance verified successfully!");
    } catch (error) {
      toast.error(error.response?.data?.error || "Verification failed");
      
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };


  // Add useEffect to fetch IPE pricing
  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true },
        );
        
        // Find IPE pricing
        const ipePricingData = Array.isArray(response.data)
          ? response.data.find((item) => item.key === "ipe")
          : response.data;

        const ipePricing =
          ipePricingData?.key === "ipe" ? ipePricingData : null;

        if (ipePricing && ipePricing.prices) {
          setAmount(ipePricing.prices.agent);
        }
      } catch (error) {
        toast.error("Failed to fetch current price");
      } finally {
        setPriceLoading(false);
      }
    };

    fetchPrices();
  }, []);

  return (
    <>
      <div className="w-full rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">IPE Clearance</h2>
            <p className="text-sm text-gray-500 mt-1">Verify your IPE tracking details</p>
          </div>
          <div className="bg-sky-50 px-4 py-2 rounded-lg border border-sky-100 text-sky-700 font-bold text-lg shadow-sm">
            {priceLoading ? "..." : `₦${amount.toLocaleString()}`}
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mt-2">

            <div className="flex items-stretch gap-3">
              <input
                type="text"
                className="flex-1 min-w-0 pl-4 py-3 border border-gray-300 focus:border-[#2eca7f] focus:ring-1 focus:ring-[#2eca7f] rounded-lg outline-none transition-all text-gray-700"
                placeholder="Enter NIN/Tracking id and Send"
                required
                name="trackingId"
                value={formData.trackingId}
                onChange={handleInputChange}
                inputMode="text"
              />
              <button
                type="submit"
                disabled={loading}
                className={`flex-shrink-0 px-6 rounded-lg font-semibold text-white cursor-pointer transition-colors ${
                  loading ? "bg-gray-400" : "bg-[#2eca7f] hover:bg-[#25b571]"
                }`}
              >
                {loading ? (
                  <AiOutlineLoading3Quarters className="animate-spin text-xl" />
                ) : (
                  "Process"
                )}
              </button>
            </div>
          </div>
        </form>
        <ToastContainer />
      </div>

      {/* History Table */}
      <div className="w-full overflow-hidden">
        <IPEHistoryTable />
      </div>


      <Modal
        open={isSuccessModalVisible}
        onCancel={() => setIsSuccessModalVisible(false)}
        footer={[
          <button
            key="ok"
            onClick={() => setIsSuccessModalVisible(false)}
            className="bg-sky-500 hover:bg-sky-600 text-white font-medium py-2 px-8 rounded-xl cursor-pointer"
          >
            OK
          </button>,
        ]}
      >
        <div className="py-4 text-center">
          <h1 className="text-3xl font-bold text-sky-500 mb-5">
            IPE Clearance
          </h1>

          {verificationResult && (
            <>
              <p className="text-[17px] text-green-900 bg-green-100 mb-5 p-3 rounded">
                {verificationResult.description}
              </p>
            </>
          )}

          <div className="text-gray-600 text-xl mb-4 font-semibold">
            IPE Clearance Successful!
          </div>
        </div>
      </Modal>
    </>
  );
}

export default IPEClearance;
